import React, { createContext, ReactNode, useContext, useEffect, useState } from "react";
import { LoyaltyBrandAPIPromiseClient } from "../gen/witbooking/backoffice/loyalty/brand/v1/loyalty_brand_api_grpc_web_pb";
import {
  BookAndJoinType,
  GetLoyaltyBrandsManagedByPropertyRequest,
  RegistrationTextType,
} from "../gen/witbooking/backoffice/loyalty/brand/v1/loyalty_brand_api_pb";
import { ACCESS_DENIED } from "../lib/auth";
import { useGrpcClient, usePropertyGrpcCall } from "../lib/grpc";
import { useKeycloakAuthz } from "./keycloak-provider";

export interface IPropertyLoyaltyBrand {
  managedByList: string[];
  relatedDomainsList: string[];
  ticker: string;
  id: string;
  externalSystemId: string;
  transactionExpirationLimit: number;
  usedByList: string[];
  mainDomain?: string;
  authorizationServiceDomain?: string;
  bookAndJoinType: BookAndJoinType;
  registrationTextType: RegistrationTextType;
  widgetConfiguration?: {
    url: string;
    tierAgencyId: string;
    tierCompanyId: string;
  };
}

export interface LoyaltyBrandProviderProps {
  propertyTicker: string;
  children: ReactNode | ((value: LoyaltyBrandValue) => ReactNode);
}

export type LoyaltyBrandValue = IPropertyLoyaltyBrand | undefined | null;
const LoyaltyBrand = createContext<LoyaltyBrandValue>(undefined);

export function LoyaltyBrandProvider({ propertyTicker, children }: LoyaltyBrandProviderProps) {
  const [data, setData] = useState<LoyaltyBrandValue>(undefined);
  const keycloakAuthz = useKeycloakAuthz();
  const grpcClient = useGrpcClient(LoyaltyBrandAPIPromiseClient);
  const getLoyaltyBrandsManagedByProperty = usePropertyGrpcCall(
    grpcClient,
    grpcClient.getLoyaltyBrandsManagedByProperty
  );

  useEffect(() => {
    async function load() {
      try {
        const request = new GetLoyaltyBrandsManagedByPropertyRequest();
        request.setPropertyTicker(propertyTicker);
        const response = await getLoyaltyBrandsManagedByProperty(request);
        const brandsList = response
          .toObject()
          .brandsList.map(
            ({
              managedByList,
              relatedDomainsList,
              ticker,
              transactionExpirationLimit,
              usedByList,
              id,
              externalSystemId,
              mainDomain,
              authorizationServiceDomain,
              bookAndJoinType,
              registrationTextType,
            }) => {
              // Get raw brand to access widgetConfiguration
              const rawBrand = response.getBrandsList().find(b => b.getTicker() === ticker);
              const widgetConfig = rawBrand?.getWidgetConfiguration();

              return {
                managedByList,
                relatedDomainsList,
                ticker,
                transactionExpirationLimit,
                usedByList,
                id,
                externalSystemId,
                mainDomain,
                authorizationServiceDomain,
                bookAndJoinType,
                registrationTextType,
                widgetConfiguration: widgetConfig
                  ? {
                      url: widgetConfig.getUrl(),
                      tierAgencyId: widgetConfig.getTierAgencyId(),
                      tierCompanyId: widgetConfig.getTierCompanyId(),
                    }
                  : undefined,
              };
            }
          );
        setData(brandsList[0]);
      } catch (e) {
        if (e === ACCESS_DENIED) {
          console.error("Access denied", e);
        } else {
          console.error("Error retrieving rpt", e);
        }
      }
    }

    load();
  }, [getLoyaltyBrandsManagedByProperty, keycloakAuthz, propertyTicker]);

  return (
    <LoyaltyBrand.Provider value={data}>
      {typeof children === "function" ? children(data) : children}
    </LoyaltyBrand.Provider>
  );
}

export function useLoyaltyBrand(): LoyaltyBrandValue {
  return useContext(LoyaltyBrand);
}
